"use client";
import Image from "next/image";
import { motion, Variants } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FaLinkedinIn, FaFacebookF, FaGithub } from "react-icons/fa6";
import type { IconType } from "react-icons";
import { AppButton } from "@/components";
import { TeamMember } from "@/lib/types";

interface TeamHeroProps {
  member: TeamMember;
}

const SOCIAL_ICONS: Record<string, IconType> = {
  LinkedIn: FaLinkedinIn,
  Facebook: FaFacebookF,
  GitHub: FaGithub,
};

// Staggered on-mount reveal — the profile opens above the fold, so this plays on
// load rather than on scroll (mirrors TeamInfoPanel's line easing).
const line: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TeamHero = ({ member }: TeamHeroProps) => {
  const socials = member.socials.filter((s) => s.platform in SOCIAL_ICONS);

  return (
    <section className="relative overflow-hidden bg-background pt-14 pb-10 md:pt-16 md:pb-14">
      <div className="mx-auto grid w-full max-w-360 items-center gap-12 px-4 lg:grid-cols-12 lg:gap-16 lg:px-10">
        {/* Copy */}
        <div className="lg:col-span-8">
          <motion.p
            variants={line}
            custom={0}
            initial="hidden"
            animate="visible"
            className="text-sm font-semibold uppercase tracking-[0.25em] text-primary"
          >
            {member.designation}
          </motion.p>

          <motion.h1
            variants={line}
            custom={1}
            initial="hidden"
            animate="visible"
            className="mt-4 font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-7xl xl:text-8xl lg:whitespace-nowrap"
          >
            {member.name}
          </motion.h1>

          <motion.p
            variants={line}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-6 max-w-md text-lg text-muted-foreground"
          >
            {member.tagline}
          </motion.p>

          {/* Meta + socials */}
          <motion.div
            variants={line}
            custom={3}
            initial="hidden"
            animate="visible"
            className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-6"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Experience
              </p>
              <p className="text-lg font-bold">{member.experience}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Based in
              </p>
              <p className="text-lg font-bold">{member.location}</p>
            </div>
            {socials.length > 0 && (
              <div className="flex gap-3">
                {socials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.platform];
                  return (
                    <a
                      key={s.platform}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.platform}
                      className="grid h-11 w-11 place-items-center rounded-full bg-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-primary hover:text-black hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)]"
                    >
                      <Icon className="h-4.5 w-4.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </motion.div>

          <motion.div
            variants={line}
            custom={4}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-wrap gap-4"
          >
            <AppButton href="/contact" className="w-48" buttonClass="h-12">
              <span>Work with us</span>
              <ArrowUpRight className="h-4 w-4" />
            </AppButton>
            <AppButton
              href="/about-us#team"
              className="w-48"
              buttonClass="h-12 bg-transparent text-black"
              shadowClass="bg-black/20"
            >
              <span>The whole team</span>
            </AppButton>
          </motion.div>
        </div>

        {/* Portrait — pushed to the outer edge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-4"
        >
          <div className="relative mx-auto aspect-4/5 w-full max-w-sm overflow-hidden rounded-3xl border-2 border-black bg-neutral-200 shadow-[10px_10px_0_0_rgba(0,0,0,1)] lg:ml-auto lg:mr-0">
            <Image
              src={member.image}
              alt={member.name}
              fill
              sizes="(max-width: 1024px) 90vw, 360px"
              priority
              className="object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/45 via-transparent to-transparent" />
            {/* ID badge */}
            <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black tracking-[0.2em] text-black">
              {member.id}
            </span>
            {/* Accent chip */}
            <span
              className="absolute right-4 top-4 h-3.5 w-3.5 rounded-full ring-2 ring-white/60"
              style={{ backgroundColor: member.accentColor }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamHero;
